import { useEffect } from 'react';
import { supabase } from '../services/supabase';
import { syncBackgrounds } from '../services/backgroundSync';
import { onImagesChanged } from '../services/mediaStore';

const DEBOUNCE_MS = 1500;

/**
 * Keeps this login's background images in step with the cloud (see
 * services/backgroundSync): once at start, when the connection returns, when
 * the window regains focus, and shortly after an image is added or removed.
 * Silent by design: it is a convenience and retries by itself.
 */
export default function useBackgroundSync() {
  useEffect(() => {
    if (!supabase) return undefined;
    let cancelled = false;
    let userId = null;
    let running = false;
    let again = false;
    let timer = null;

    const run = async () => {
      if (cancelled || !userId) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (running) { again = true; return; }
      running = true;
      try {
        await syncBackgrounds({ userId });
      } catch {
        // offline or a storage hiccup: the next trigger retries
      } finally {
        running = false;
        if (again && !cancelled) { again = false; schedule(); }
      }
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, DEBOUNCE_MS);
    };

    const begin = (session) => {
      const id = session && session.user && session.user.id;
      if (id && id !== userId) { userId = id; run(); }
    };
    supabase.auth.getSession().then(({ data }) => begin(data && data.session)).catch(() => {});
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') setTimeout(() => begin(session), 0);
    });

    // An image being saved by the sync itself also fires this; the extra pass finds nothing to do.
    const offImages = onImagesChanged(() => { if (!running) schedule(); });
    window.addEventListener('online', schedule);
    window.addEventListener('focus', schedule);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      listener.subscription.unsubscribe();
      offImages();
      window.removeEventListener('online', schedule);
      window.removeEventListener('focus', schedule);
    };
  }, []);
}
